/**
 * useChatSocket Hook
 *
 * WebSocket connection hook for AI Chatbot.
 * Handles connection, messaging, streaming, and error handling.
 * Phase 7: Frontend Integration
 *
 * @module UseChatSocket
 */

'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { io, Socket } from 'socket.io-client';
import { logger } from '@axon/ui/utils';

import { useAuthStore } from '@/stores/auth.store';
import { getWebSocketUrl } from '@/utils/websocket-url';

import type {
  UseChatSocketOptions,
  SocketConnectData,
  SocketChatResponse,
  SocketChunkData,
  SocketTypingData,
  SocketError,
  ChatLanguage,
  ProactiveSuggestion,
  NavigationData,
} from '../types/chatbot.types';

// ============================================================================
// Constants
// ============================================================================

/** WebSocket namespace for AI Chatbot */
const CHATBOT_WS_NAMESPACE = 'ai-chatbot';

/** Maximum reconnection attempts */
const MAX_RECONNECTION_ATTEMPTS = 5;

/** Reconnection delay in ms */
const RECONNECTION_DELAY_MS = 1000;

/** Cap for socket.io's exponential reconnection backoff (ms). */
const RECONNECTION_DELAY_MAX_MS = 10000;

/** Connection timeout in ms */
const CONNECTION_TIMEOUT_MS = 10000;

// ============================================================================
// WebSocket State
// ============================================================================

interface SocketState {
  isConnected: boolean;
  isConnecting: boolean;
  connectionAttempts: number;
}

// ============================================================================
// Hook Implementation
// ============================================================================

/**
 * WebSocket hook for AI Chatbot communication
 *
 * @example
 * const { socket, isConnected, connect, sendMessage } = useChatSocket({
 *   onConnect: (data) => console.log('Connected:', data.conversationId),
 *   onMessage: (message) => console.log('Message:', message),
 *   onChunk: (chunk) => console.log('Chunk:', chunk),
 *   onError: (error) => console.error('Error:', error),
 * });
 */
export function useChatSocket(options: UseChatSocketOptions = {}) {
  const { token, tenantId, user } = useAuthStore();
  const socketRef = useRef<Socket | null>(null);
  const optionsRef = useRef(options);

  // Keep options ref up to date
  optionsRef.current = options;

  const [state, setState] = useState<SocketState>({
    isConnected: false,
    isConnecting: false,
    connectionAttempts: 0,
  });

  // Use ref for connection state to avoid callback identity changes causing reconnection loops
  const stateRef = useRef(state);
  stateRef.current = state;

  // Track which language the current socket is connected with
  const connectedLanguageRef = useRef<ChatLanguage | null>(null);

  /**
   * Establish WebSocket connection
   */
  const connect = useCallback(
    (language: ChatLanguage = 'en') => {
      // If already connected but language changed, disconnect first and reconnect
      if (socketRef.current?.connected && connectedLanguageRef.current !== language) {
        logger.info('[useChatSocket] Language changed, reconnecting', {
          from: connectedLanguageRef.current,
          to: language,
        });
        socketRef.current.disconnect();
        socketRef.current = null;
        connectedLanguageRef.current = null;
      }

      // Skip if already connected (same language) or connecting
      if (socketRef.current?.connected || stateRef.current.isConnecting) {
        logger.debug('[useChatSocket] Already connected or connecting');
        return;
      }

      // Require authentication
      if (!token || !tenantId) {
        logger.warn('[useChatSocket] Cannot connect: Missing auth token or tenantId');
        return;
      }

      // Check max reconnection attempts
      if (stateRef.current.connectionAttempts >= MAX_RECONNECTION_ATTEMPTS) {
        logger.error('[useChatSocket] Max reconnection attempts reached');
        optionsRef.current.onError?.({
          code: 'MAX_ATTEMPTS',
          message: 'Maximum connection attempts reached. Please refresh the page.',
        });
        return;
      }

      setState((prev) => ({
        ...prev,
        isConnecting: true,
        connectionAttempts: prev.connectionAttempts + 1,
      }));

      const wsUrl = getWebSocketUrl();
      const socket = io(`${wsUrl}/${CHATBOT_WS_NAMESPACE}`, {
        // `auth` as a function → socket.io invokes it on every (re)connect, so
        // reconnects after a backend deploy or token rotation use a FRESH token
        // from the store instead of the stale one captured when the socket was
        // created. Without this, an expired token left the chatbot stuck
        // "connecting" until the user logged out/in.
        auth: (cb) => {
          const s = useAuthStore.getState();
          cb({ token: s.token, tenantId: s.tenantId, userId: s.user?.id });
        },
        query: {
          platform: 'web-tms',
          language,
        },
        transports: ['websocket'],
        reconnection: true,
        // Retry indefinitely: a backend deploy / network blip can exceed a
        // handful of attempts, and giving up left the chatbot permanently
        // "connecting" until a full re-login. Exponential backoff is capped
        // by reconnectionDelayMax so we don't hammer the server.
        reconnectionAttempts: Infinity,
        reconnectionDelay: RECONNECTION_DELAY_MS,
        reconnectionDelayMax: RECONNECTION_DELAY_MAX_MS,
        timeout: CONNECTION_TIMEOUT_MS,
      });

      // Connection established
      socket.on('connect', () => {
        logger.info('[useChatSocket] Connected to AI Chatbot WebSocket', { language });
        connectedLanguageRef.current = language;
        setState({
          isConnected: true,
          isConnecting: false,
          connectionAttempts: 0,
        });
      });

      // Chat session initialized
      socket.on('chat:connected', (data: SocketConnectData) => {
        logger.info('[useChatSocket] Chat session initialized', {
          conversationId: data.conversationId,
        });
        optionsRef.current.onConnect?.(data);
      });

      // Full message received (non-streaming)
      socket.on('chat:response', (data: SocketChatResponse) => {
        logger.debug('[useChatSocket] Received chat response');
        optionsRef.current.onMessage?.(data.message);
      });

      // Streaming chunk received
      socket.on('chat:response_chunk', (data: SocketChunkData) => {
        optionsRef.current.onChunk?.(data.chunk);
      });

      // Stream complete
      socket.on(
        'chat:response_complete',
        (data: {
          conversationId?: string;
          messageId?: string;
          type?: string;
          metadata?: Record<string, unknown>;
        }) => {
          logger.debug('[useChatSocket] Stream complete', { type: data?.type });
          optionsRef.current.onStreamComplete?.({
            conversationId: data?.conversationId,
            messageId: data?.messageId,
            type: data?.type,
            metadata: data?.metadata,
          });
        }
      );

      // Post-hoc enrichment (follow-ups, data insight) for a message
      // that already shipped with static fallbacks.
      socket.on(
        'chat:message_enriched',
        (data: { messageId: string; metadata: Record<string, unknown> }) => {
          logger.debug('[useChatSocket] Message enriched', { messageId: data?.messageId });
          if (data?.messageId && data?.metadata) {
            optionsRef.current.onMessageEnriched?.(data);
          }
        }
      );

      // Typing indicator
      socket.on('chat:typing', (data: SocketTypingData) => {
        optionsRef.current.onTyping?.(data.isTyping);
      });

      // Error handling
      socket.on('chat:error', (error: SocketError) => {
        logger.error('[useChatSocket] Chat error', {
          code: error?.code,
          message: error?.message,
        });
        optionsRef.current.onError?.(error);
      });

      // Action confirmation request from server (Phase 18)
      socket.on(
        'chat:action_confirm',
        (data: {
          confirmationId: string;
          actionType: string;
          entityType: string;
          entityId: string;
          safetyLevel: string;
          requiresReason: boolean;
          description: string;
          currentState?: Record<string, unknown>;
          conversationId: string;
        }) => {
          logger.debug('[useChatSocket] Action confirmation request', {
            actionType: data?.actionType,
          });
          optionsRef.current.onActionConfirm?.(data);
        }
      );

      // Action execution result from server (Phase 18)
      socket.on(
        'chat:action_result',
        (data: {
          success: boolean;
          actionType: string;
          entityType: string;
          entityId: string;
          changes?: Record<string, { from: unknown; to: unknown }>;
          message: string;
          conversationId: string;
        }) => {
          logger.debug('[useChatSocket] Action result', { success: data?.success });
          optionsRef.current.onActionResult?.(data);
        }
      );

      // Proactive suggestion from server (Phase 18)
      socket.on('chat:proactive_suggestion', (data: ProactiveSuggestion) => {
        logger.debug('[useChatSocket] Proactive suggestion', { type: data?.type });
        optionsRef.current.onProactiveSuggestion?.(data);
      });

      // Navigation command from server (Phase 19)
      socket.on('chat:navigate', (data: NavigationData) => {
        logger.debug('[useChatSocket] Navigation command', { path: data?.path });
        optionsRef.current.onNavigate?.(data);
      });

      // Workflow completion from server (Phase 19F)
      socket.on('chat:workflow_complete', (data: Record<string, unknown>) => {
        logger.debug('[useChatSocket] Workflow complete', {
          success: data?.success,
          totalSteps: data?.totalSteps,
        });
        optionsRef.current.onWorkflowComplete?.(data);
      });

      // Disconnection
      socket.on('disconnect', (reason) => {
        logger.info('[useChatSocket] Disconnected', { reason });
        setState((prev) => ({
          ...prev,
          isConnected: false,
          isConnecting: false,
        }));
        optionsRef.current.onDisconnect?.();
      });

      // Connection error.
      // B-244: socket.io fires `connect_error` on EVERY failed (re)connection
      // attempt, including the first transient one right after a page refresh
      // while the handshake is still settling. Surfacing it to the user
      // immediately flashed a "connection error" even though the manager
      // reconnects on its own moments later (reconnection:true,
      // reconnectionAttempts:5). Keep the connection still marked as
      // "connecting" and stay quiet here — let socket.io retry — and only
      // raise a user-facing error once it has genuinely exhausted every
      // attempt (`reconnect_failed`, wired below).
      socket.on('connect_error', (error) => {
        logger.warn('[useChatSocket] Connection attempt failed, retrying', {
          message: error?.message,
        });
        setState((prev) => ({
          ...prev,
          isConnected: false,
          isConnecting: true,
        }));
      });

      // Reconnection genuinely exhausted — now it's a real failure worth
      // showing the user (B-244). Emitted on the manager, not the socket.
      socket.io.on('reconnect_failed', () => {
        logger.error('[useChatSocket] Reconnection failed after all attempts');
        setState((prev) => ({
          ...prev,
          isConnected: false,
          isConnecting: false,
        }));
        optionsRef.current.onError?.({
          code: 'CONNECTION_ERROR',
          message: 'Failed to connect to chat server',
        });
      });

      socketRef.current = socket;
    },
    [token, tenantId, user?.id]
  );

  /**
   * Disconnect WebSocket
   */
  const disconnect = useCallback(() => {
    if (socketRef.current) {
      logger.info('[useChatSocket] Disconnecting');
      socketRef.current.disconnect();
      socketRef.current = null;
      connectedLanguageRef.current = null;
      setState({
        isConnected: false,
        isConnecting: false,
        connectionAttempts: 0,
      });
    }
  }, []);

  /**
   * Send chat message via WebSocket
   */
  const sendMessage = useCallback(async (content: string): Promise<void> => {
    if (!socketRef.current?.connected) {
      throw new Error('Not connected to chat server');
    }

    logger.debug('[useChatSocket] Sending message', {
      contentLength: content.length,
    });

    socketRef.current.emit('chat:message', { content });
  }, []);

  /**
   * Leave the current conversation on the backend.
   * Resets the backend session's conversationId to null so the next message
   * creates a fresh conversation. Used when the current conversation is closed/archived.
   */
  const leaveConversation = useCallback((conversationId: string) => {
    if (!socketRef.current?.connected || !conversationId) {
      return;
    }

    logger.info('[useChatSocket] Leaving closed conversation', { conversationId });
    socketRef.current.emit('chat:leave', { conversationId });
  }, []);

  /**
   * Send action confirmation response (Phase 18)
   */
  const confirmAction = useCallback(
    (confirmationId: string, confirmed: boolean, reason?: string) => {
      if (!socketRef.current?.connected) {
        logger.warn('[useChatSocket] Cannot confirm action: Not connected');
        return;
      }

      logger.debug('[useChatSocket] Sending action confirmation', { confirmationId, confirmed });
      socketRef.current.emit('chat:action_response', { confirmationId, confirmed, reason });
    },
    []
  );

  /**
   * Send screen context update to server (Phase 19C)
   */
  const sendScreenContext = useCallback((currentPath: string) => {
    if (!socketRef.current?.connected) {
      return;
    }

    socketRef.current.emit('chat:screen_context', { currentPath });
  }, []);

  /**
   * Notify server to clear chat history and user memories
   */
  const clearHistory = useCallback(() => {
    if (!socketRef.current?.connected) {
      return;
    }

    socketRef.current.emit('chat:clear_history');
  }, []);

  /**
   * Send feedback for a message
   */
  const sendFeedback = useCallback(
    (messageId: string, rating: 'helpful' | 'not_helpful', comment?: string) => {
      if (!socketRef.current?.connected) {
        logger.warn('[useChatSocket] Cannot send feedback: Not connected');
        return;
      }

      logger.debug('[useChatSocket] Sending feedback', { messageId, rating });
      socketRef.current.emit('chat:feedback', { messageId, rating, comment });
    },
    []
  );

  // Cleanup on unmount + beforeunload (ensure disconnect reaches server)
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (socketRef.current?.connected) {
        socketRef.current.disconnect();
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
      }
    };
  }, []);

  return {
    socket: socketRef.current,
    isConnected: state.isConnected,
    isConnecting: state.isConnecting,
    connectionAttempts: state.connectionAttempts,
    connect,
    disconnect,
    sendMessage,
    confirmAction,
    sendFeedback,
    sendScreenContext,
    clearHistory,
    leaveConversation,
  };
}

export type { UseChatSocketOptions };
